import type {
	BaseEventPayload,
	DropTargetGetFeedbackArgs,
	DropTargetLocalizedData,
	ElementDragType,
} from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useMergeRefs } from "@floating-ui/react";
import clsx from "clsx";
import { motion, type TargetAndTransition, type VariantLabels } from "motion/react";
import { Fragment, type JSX, useEffect, useRef, useState } from "react";
import type { ChildrenProps } from "../types";
import { DragOverIndicator } from "./drag-over-indicator";
import type { DraggableItem } from "./types";

/** Props Interface */
interface DroppableProps extends ChildrenProps {
	className?: string;
	style?: React.CSSProperties;
	accept: Array<DraggableItem>;
	acceptsSameOrigin?: boolean;
	initial?: boolean | VariantLabels | undefined;
	dragOverIndicator?: (() => JSX.Element) | null;
	animate?: boolean | VariantLabels | TargetAndTransition | undefined;
	exit?: VariantLabels | TargetAndTransition | undefined;
	handleOnDrop?: (payload: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) => void;
	layoutId?: string;
	layout?: boolean | "position" | "size" | "preserve-aspect" | undefined;
	id?: string;
	isDisabled?: boolean;
	onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onDoubleClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	canDrop?: (data: DropTargetGetFeedbackArgs<ElementDragType>) => boolean;
}

export function Droppable({
	children,
	className,
	style,
	accept,
	acceptsSameOrigin = true,
	handleOnDrop,
	initial,
	animate,
	exit,
	dragOverIndicator,
	layoutId,
	layout,
	isDisabled,
	canDrop,
	...rest
}: DroppableProps) {
	/** State */
	const [isOver, setIsOver] = useState(false);

	/** Hooks */
	const ref = useRef<HTMLDivElement>(null);
	const refs = useMergeRefs([ref]);

	/** Effects */
	useEffect(() => {
		if (isDisabled) return;
		const el = ref.current;
		if (!el) return;

		return dropTargetForElements({
			canDrop: (data) => {
				if (canDrop) {
					if (!canDrop(data)) return false;
				}
				if (!acceptsSameOrigin) {
					if (el.contains(data.source.element)) return false;
				}
				if (accept.includes(data.source.data.type as DraggableItem)) return true;
				return false;
			},
			element: el,
			onDrag: (e) => {
				if (!el.contains(e.source.element)) return;
				setIsOver(true);
			},
			onDragEnter: () => {
				setIsOver(true);
			},
			onDragLeave: () => {
				setIsOver(false);
			},
			onDrop: (payload) => {
				handleOnDrop?.(payload);
				setIsOver(false);
			},
		});
	}, [accept, acceptsSameOrigin, handleOnDrop, isDisabled]);

	/** Constants */
	const Indicator =
		dragOverIndicator !== null ? dragOverIndicator || DragOverIndicator : () => null;

	/** Render */
	return (
		<Fragment>
			<motion.div
				className={clsx("relative", className)}
				ref={refs}
				layoutId={layoutId}
				layout={layout}
				style={{ ...style, contentVisibility: "auto" }}
				initial={initial}
				animate={animate}
				exit={exit}
				{...rest}
			>
				{isOver && <Indicator />}
				{children}
			</motion.div>
		</Fragment>
	);
}
